import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { HomeModernIcon, WrenchScrewdriverIcon } from "@heroicons/react/24/outline";

import { useSignalR } from "../../contexts/SignalRContext";
import {
  IProductionDashboardAssembly,
  IProductionDashboardBay,
  IProductionDashboardBayVehicle,
} from "../../models/ProductionDashboard";
import { IPlannerVehicleRow } from "../../models/ProductionPlanner";
import { assemblyElapsedSeconds, formatAssemblyDuration } from "../../utils/assemblyElapsed";
import { assemblySteps, completedAssemblyNames, displayedAssemblies } from "../../utils/assemblySteps";
import { estimatedRemainingSeconds, formatRemaining } from "../../utils/estimatedRemaining";
import {
  BayCardState,
  bayInProgressVehicles,
  bayState,
  BayVehicleState,
  vehicleState,
} from "../../utils/bayCardState";
import classNames from "../../utils/classNames";
import {
  complexityChipClasses,
  complexityLabel,
  lineChipClasses,
  lineLabel,
} from "../../utils/vehicleDisplay";
import Avatar from "../user/Avatar";
import { BAY_STATE_LIGHT, bayStateLabel } from "./bayStateStyles";
import OpenVehicleJobButtons from "./OpenVehicleJobButtons";

interface BayManagerCardProps {
  bay: IProductionDashboardBay;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  busyVehicleIds?: Set<string>;
  // TME customers don't use the Non-Assembly bucket, so it stays out of their steppers.
  hideNonAssembly?: boolean;
}

const plateClasses =
  "inline-block rounded border border-yellow-500 bg-yellow-300 px-2 font-bold tracking-wider text-gray-900 whitespace-nowrap";

const vehicleName = (v: { combinedMake: string | null; combinedModel: string | null }) =>
  [v.combinedMake, v.combinedModel].filter(Boolean).join(" ");

function StatePill({ state }: { state: BayCardState }) {
  const { t } = useTranslation("production");
  const s = BAY_STATE_LIGHT[state];

  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0",
        s.pill
      )}
    >
      <span className={classNames("h-1.5 w-1.5 rounded-full", s.dot)} />
      {t(bayStateLabel(state))}
    </span>
  );
}

// What the timer panel says under the clock, by state.
function timerNote(
  t: (key: string, opts?: Record<string, unknown>) => string,
  vehicle: IProductionDashboardBayVehicle,
  state: BayVehicleState,
  elapsedSeconds: number
): string {
  const targetSeconds = (vehicle.currentAssemblyAverageMinutes ?? 0) * 60;
  switch (state) {
    case "over":
      return t("{{time}} over target", {
        time: formatAssemblyDuration(elapsedSeconds - targetSeconds),
      });
    case "warning":
      return t("Past the {{minutes}}m warning time", {
        minutes: vehicle.currentAssemblyWarningMinutes,
      });
    case "waiting":
      return t("Finished here - move on to the next assembly");
    case "paused":
      return t("Timer stopped - {{remaining}} parts left", {
        remaining: Math.max(
          0,
          vehicle.currentAssemblyPartsTotal - vehicle.currentAssemblyPartsRemoved
        ),
      });
    case "notStarted":
      return t("No time recorded on this assembly yet");
    default:
      return t("{{removed}} of {{total}} parts off this assembly", {
        removed: vehicle.currentAssemblyPartsRemoved,
        total: vehicle.currentAssemblyPartsTotal,
      });
  }
}

function BayVehiclePanel({
  vehicle,
  assemblies,
  now,
  hideNonAssembly,
}: {
  vehicle: IProductionDashboardBayVehicle;
  assemblies: IProductionDashboardAssembly[];
  now: DateTime;
  hideNonAssembly: boolean;
}) {
  const { t } = useTranslation("production");
  const { activeJobSessionsByVehicle } = useSignalR();
  const activeJobUsers = activeJobSessionsByVehicle[vehicle.id] ?? [];

  const state = vehicleState(vehicle, now);
  const s = BAY_STATE_LIGHT[state];
  const elapsedSeconds = assemblyElapsedSeconds(vehicle, now);
  const targetMinutes = vehicle.currentAssemblyAverageMinutes;
  const timerPercent = targetMinutes
    ? Math.min(100, (100 * elapsedSeconds) / (targetMinutes * 60))
    : 0;
  const shown = { ...vehicle, assemblies: displayedAssemblies(vehicle, hideNonAssembly) };
  const steps = assemblySteps(shown, assemblies);
  const doneNames = completedAssemblyNames(shown);
  const remainingSeconds = estimatedRemainingSeconds(shown.assemblies, now);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-start gap-2.5">
        <span className={classNames(plateClasses, "text-base")}>{vehicle.vrm || t("No VRM")}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-gray-900 truncate">
            {vehicleName(vehicle) || vehicle.stockNumber || "-"}
            {vehicle.bodyStyle && <span className="ml-1 text-gray-500">{vehicle.bodyStyle}</span>}
          </div>
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            <span
              className={classNames(
                "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5",
                complexityChipClasses(vehicle.complexity)
              )}
            >
              {complexityLabel(vehicle.complexity)}
            </span>
            <span
              className={classNames(
                "text-[10px] font-medium rounded px-1.5 py-0.5",
                lineChipClasses(vehicle.productionLine)
              )}
            >
              {lineLabel(vehicle.productionLine)}
            </span>
          </div>
        </div>
      </div>

      {/* Current assembly + timer */}
      <div className={classNames("rounded-md px-3 py-2.5 flex flex-col gap-1.5", s.panel)}>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs font-semibold text-gray-900 truncate">
            {vehicle.currentAssemblyName ?? t("No assembly")}
          </span>
          {vehicle.assemblyCount > 0 && vehicle.currentAssemblyIndex && (
            <span className="text-[11px] text-gray-500 shrink-0">
              {t("Assembly {{index}} of {{count}}", {
                index: vehicle.currentAssemblyIndex,
                count: vehicle.assemblyCount,
              })}
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className={classNames("text-3xl font-semibold leading-none tabular-nums", s.timer)}>
            {formatAssemblyDuration(elapsedSeconds)}
          </span>
          {targetMinutes ? (
            <span className="text-xs text-gray-500">
              {t("/ {{minutes}}m target", { minutes: targetMinutes })}
            </span>
          ) : null}
        </div>
        <div className="h-1 rounded bg-gray-900/10 overflow-hidden">
          <div className={classNames("h-full", s.step)} style={{ width: `${timerPercent}%` }} />
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className={classNames("text-[11px]", s.note)}>
            {timerNote(t, vehicle, state, elapsedSeconds)}
          </span>
          {remainingSeconds !== null && (
            <span
              className="text-[11px] text-gray-600 tabular-nums shrink-0"
              title={t("Estimated from the targets of the assemblies still to finish")}
            >
              {t("{{time}} left", { time: formatRemaining(remainingSeconds) })}
            </span>
          )}
        </div>
      </div>

      {/* Assembly stepper: the whole strip-down rather than one column of it */}
      {steps.length > 0 && (
        <div className="flex gap-0.5">
          {steps.map((step, i) => (
            <div key={i} className="flex-1 min-w-0 flex flex-col gap-1" title={step.label}>
              <div
                className={classNames(
                  "h-2 rounded-sm",
                  step.state === "done"
                    ? "bg-emerald-300"
                    : step.state === "current"
                      ? s.step
                      : "bg-gray-200"
                )}
              />
              <div
                className={classNames(
                  "text-[10px] text-center truncate",
                  step.state === "current"
                    ? "font-semibold text-gray-900"
                    : step.state === "done"
                      ? "text-emerald-700"
                      : "text-gray-400"
                )}
              >
                {step.label}
              </div>
            </div>
          ))}
        </div>
      )}
      {doneNames.length > 0 && (
        <div className="text-[11px] text-gray-600 truncate" title={doneNames.join(", ")}>
          <span className="font-semibold text-emerald-700">{t("Done")}:</span>{" "}
          {doneNames.join(" · ")}
        </div>
      )}

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between text-[11px] text-gray-600">
          <span className="font-semibold">{t("{{percent}}% stripped", { percent: vehicle.percentComplete })}</span>
          <span className="tabular-nums">
            {t("{{removed}}/{{total}} parts", {
              removed: vehicle.partsRemoved,
              total: vehicle.partsTotal,
            })}
          </span>
        </div>
        <div className="h-1.5 rounded bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-emerald-500"
            style={{ width: `${Math.min(100, vehicle.percentComplete)}%` }}
          />
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-2.5 border-t border-gray-100">
        {activeJobUsers.length > 0 ? (
          <div className="flex items-center gap-1">
            <WrenchScrewdriverIcon className="w-3.5 h-3.5 text-amber-500 shrink-0" />
            <div className="flex">
              {activeJobUsers.map((userId, key) => (
                <Avatar
                  key={key}
                  userId={userId}
                  classes="w-6 h-6 rounded-full [&:not(:first-child)]:-ml-1.5"
                  textClasses="text-[15px]"
                />
              ))}
            </div>
          </div>
        ) : (
          <span className="text-[11px] text-gray-500">{t("No one clocked on")}</span>
        )}
        <OpenVehicleJobButtons vehicleId={vehicle.id} size="md" />
      </div>
    </div>
  );
}

function DueNextRow({ vehicle }: { vehicle: IPlannerVehicleRow }) {
  const { t } = useTranslation("production");

  return (
    <div className="flex items-center gap-2 border border-gray-200 rounded-md bg-white p-2">
      <span className={classNames(plateClasses, "text-sm")}>{vehicle.vrm || t("No VRM")}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-gray-900 truncate">
          {vehicleName(vehicle) || vehicle.stockNumber || "-"}
        </div>
        {vehicle.locationName && (
          <div className="text-[11px] text-gray-500 truncate">{vehicle.locationName}</div>
        )}
      </div>
      <span
        className={classNames(
          "text-[10px] font-semibold uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0",
          complexityChipClasses(vehicle.complexity)
        )}
      >
        {complexityLabel(vehicle.complexity)}
      </span>
    </div>
  );
}

// One dismantling bay: every vehicle on it, what assembly each is on and how the timer is doing,
// plus what the planner has lined up next. Empty bays say so rather than disappearing, because an
// idle bay with a queue is the thing a team leader most wants to spot.
export default function BayManagerCard({
  bay,
  assemblies,
  now,
  busyVehicleIds,
  hideNonAssembly = false,
}: BayManagerCardProps) {
  const { t } = useTranslation("production");
  const vehicles = bayInProgressVehicles(bay, busyVehicleIds);
  const state = bayState(vehicles, now);
  const s = BAY_STATE_LIGHT[state];
  const dueNext = bay.dueNext ?? [];
  const next = dueNext[0];

  return (
    <div
      className={classNames("border rounded-lg p-3.5 flex flex-col gap-3 min-h-[380px]", s.card)}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-base font-semibold text-gray-900 truncate">{bay.name}</div>
        <StatePill state={state} />
      </div>

      {vehicles.length === 0 ? (
        <div className="flex-1 flex flex-col gap-3.5">
          <div className="flex-1 flex flex-col items-center justify-center gap-1 border border-dashed border-gray-300 rounded-md p-5 text-center">
            <HomeModernIcon className="h-7 w-7 text-gray-400" />
            <div className="text-[13px] font-semibold text-gray-700">{t("Bay empty")}</div>
          </div>
          <div className="flex flex-col gap-1.5">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              {t("Due on next")} · {t("Production Planner")}
            </div>
            {next ? (
              <>
                <DueNextRow vehicle={next} />
                {dueNext.length > 1 && (
                  <div className="text-[11px] text-gray-500">
                    {t("+{{count}} more queued", { count: dueNext.length - 1 })}
                  </div>
                )}
              </>
            ) : (
              <div className="text-xs text-gray-500 border border-gray-200 rounded-md bg-white p-2.5">
                {t("Nothing planned for this bay today.")}{" "}
                <Link to="/production-planner" className="text-primary-600 hover:underline">
                  {t("Open Production Planner")}
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex-1 flex flex-col gap-4 divide-y divide-gray-200">
            {vehicles.map((v, i) => (
              <div key={v.id} className={i > 0 ? "pt-4" : undefined}>
                <BayVehiclePanel
                  vehicle={v}
                  assemblies={assemblies}
                  now={now}
                  hideNonAssembly={hideNonAssembly}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 whitespace-nowrap overflow-hidden">
            <span className="text-[10px] font-semibold uppercase tracking-wide">{t("Next")}</span>
            <span className="text-gray-700 truncate">
              {next
                ? [next.vrm, vehicleName(next)].filter(Boolean).join(" · ")
                : t("Nothing planned")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
